import { debounce } from "lodash";
import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { routerActions } from "react-router-redux";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    GroupDetailsBreadcrumbs,
    GroupsCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as GroupActions from "../../../actions/groups";
import * as HealthDistrictActions from "../../../actions/healthDistrict";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    SelectFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import {
    renderRegistrationFormSaveBlock,
    renderRegistrationFormSaveSuccess,
    renderRegistrationFormSaveWarning,
} from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import {
    IGroupReduxFormState,
    initialGroupState,
} from "../../../reducers/reactReduxForms/group";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./GroupPage.scss";

interface IGroupPageParams {
    groupId: number;
}

interface IGroupPageProps extends IRouterProps<IGroupPageParams> {
    form: Dtos.Group & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IGroupReduxFormState;

    groupId: number;

    // From state
    group: Dtos.Group;
    lookups: Dtos.Lookup[];

    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;

    savingGroup: boolean;
    saveGroupSuccess: boolean;
    saveGroupFailure: boolean;

    creatingGroup: boolean;
    createGroupSuccess: boolean;
    createGroupFailure: boolean;

    validatingGroup: boolean;

    validationFailures: Dtos.ResponseError[];

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];

    healthDistricts: Dtos.HealthDistrict[];
}

interface IGroupPageActions extends IRouterActions {
    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;

    saveGroup: GroupActions.IGroupSaveActionCreator;
    createGroup: GroupActions.IGroupCreateActionCreator;
    validateGroup: GroupActions.IGroupValidateActionCreator;

    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

    clearHealthDistricts: HealthDistrictActions.IHealthDistrictClearActionCreator;
    loadHealthDistricts: HealthDistrictActions.IHealthDistrictLoadActionCreator;

}

type GroupPageProps = IGroupPageProps & IGroupPageActions;

const reduxFormName: string = "reduxForms.group";

class GroupPage extends React.PureComponent<GroupPageProps, any> {
    initialised: boolean;
    hasBeenSubmitted: boolean;

    constructor(props: GroupPageProps) {
        super(props);

        this.state = {
            initialised: false,
            hasBeenSubmitted: false,
        };

        this.clearGroup = this.clearGroup.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.loadForm = this.loadForm.bind(this);
        this.debounceValidate = this.debounceValidate.bind(this);
    }

    componentDidMount() {
        const {
            loadGroup,
            groupId,
            createGroup,
            permissions,
            setDrawerItems,
            setBreadcrumbs,
            loadHealthDistricts
        } = this.props;
        

        loadHealthDistricts();

        if (groupId) {
            setDrawerItems(GroupSubMenu(groupId, permissions));
            setBreadcrumbs(GroupDetailsBreadcrumbs(groupId));
            loadGroup(groupId);
        } else if (!groupId) {
            setBreadcrumbs(GroupsCreateBreadcrumbs());
            createGroup();
        }
    }

    componentDidUpdate(prevProps: GroupPageProps, _prevState) {
        const {
            group,
            groupId,
            form,
            navigate,
            loadGroup,
            loadingGroup,
            loadGroupSuccess,
            createGroup,
            creatingGroup,
            createGroupSuccess,
            savingGroup,
            saveGroupSuccess,
            validationFailures,
            setDrawerItems,
            setBreadcrumbs,
            permissions,
            loadGroupFailure,
            loadGroupErrorCode,
        } = this.props;

        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
        }

        if (groupId && group?.name) {
            setDrawerItems(GroupSubMenu(groupId, permissions));
            setBreadcrumbs(GroupDetailsBreadcrumbs(groupId, group.name));
        }

        if (groupId && prevProps.groupId != groupId) {
            loadGroup(groupId);
        } else if (!groupId && prevProps.groupId) {
            createGroup();
        }

        if (!loadingGroup && prevProps.loadingGroup) {
            if (loadGroupSuccess) {
                this.loadForm();
            }
        }

        if (form != prevProps.form) {
            this.debounceValidate();
        }

        if (!savingGroup && prevProps.savingGroup) {
            if (saveGroupSuccess) {
                if (validationFailures && validationFailures.length > 0) {
                    Alert.warning(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveWarning(
                                validationFailures
                            )}
                            icon={FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE}
                        />
                    );
                } else {
                    Alert.success(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveSuccess()}
                            icon={FontAwesomeIcons.Solid.CHECK}
                        />
                    );
                }
                if (form.saveAndReturn) {
                    navigate("/groups");
                } else if (!form.saveAndReturn && groupId && groupId > 0) {
                    this.loadForm();
                } else if (
                    !form.saveAndReturn &&
                    !groupId &&
                    group &&
                    group.id > 0
                ) {
                    navigate("/groups/" + group.id + "/details");
                }
            } else {
                Alert.error(
                    <NotificationAlert
                        alertContent={this.renderErrorAlert()}
                        icon={FontAwesomeIcons.Solid.TIMES_OCTAGON}
                    />
                );
            }
        }

        if (!creatingGroup && prevProps.creatingGroup) {
            if (createGroupSuccess) {
                this.loadForm();
            }
        }
    }

    componentWillUnmount() {
        const { resetForm, loadForm } = this.props;

        this.clearGroup();
        loadForm(reduxFormName, initialGroupState);
        resetForm(reduxFormName);
    }

    clearGroup() {
        this.props.clearGroup();
        this.props.clearHealthDistricts();
    }

    showGroup(): boolean {
        const { loadingGroup, loadGroupSuccess } = this.props;

        if (!loadingGroup && loadGroupSuccess) {
            return true;
        }

        return false;
    }

    debounceValidate = debounce(async () => {
        const hasBeenSubmitted = this.state?.hasBeenSubmitted;
        const { form, validatingGroup, validateGroup } = this.props;

        let changes: Dtos.Group = {} as Dtos.Group;

        if (form && hasBeenSubmitted && !validatingGroup) {
            await validateGroup(Object.assign({ ...form }, changes));
        }
    }, 250);

    render() {
        const { groupId, group, loadingGroup, permissions } = this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.GroupView}
                subMenuTitle={group ? group.name : ""}
                subMenuItems={GroupSubMenu(groupId, permissions)}
                loading={loadingGroup}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const {
            form,
            lookups,
            validationFailures,
            formProperties,
            formState,
            changeForm,
            savingGroup,
            permissions,
            groupId,
            healthDistricts,
        } = this.props;

        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                groupId,
                permissions,
                Dtos.Permission.GroupUpdate,
                Dtos.Permission.GroupCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroup ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let healthDistrictOptions = [];
        if (form.stateId && healthDistricts) {
            const filteredHealthDistricts = healthDistricts
                ?.filter((x) => x.stateId == form?.stateId)
                ?.map((x) => x.id);
            healthDistrictOptions = generateOptionsFromLookup(
                {
                    type: "Group",
                    propertyName: "HealthDistrictId",
                    values: findLookup("Group", "HealthDistrictId", lookups)
                        ?.values?.filter((l) =>
                            filteredHealthDistricts?.includes(l.id)
                        )
                        ?.sort((a, b) => a.value.localeCompare(b.value)),
                } as Dtos.Lookup,
                form.healthDistrictId
            );
        }

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            otherNames: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            abn: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            website: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            address: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            suburb: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            phone: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-3",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            stateId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "group",
                                                                "stateId",
                                                                lookups
                                                            ),
                                                            form.stateId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            facsimileNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-3",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            postcode: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            countryId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "group",
                                                                "CountryId",
                                                                lookups
                                                            ),
                                                            form.countryId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            additionalInformation: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            researchGovernanceGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Research Governance</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "group",
                                        {
                                            healthDistrictId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.stateId,
                                                inputProps: {
                                                    options:
                                                        healthDistrictOptions,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => this.handleOnSubmit(val)}
                    onSubmitFailed={(val) => this.handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "group",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                this.props.changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                this.props.changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={this.onCancel}
                            className="btn btn-primary mr-2"
                        >
                            {" "}
                            Cancel{" "}
                        </button>
                    </div>
                </Form>
            </>
        );
    }

    renderErrorAlert(): any {
        return (
            <div>
                The form was <strong> not </strong> saved.
            </div>
        );
    }

    renderWarningAlert(): any {
        const { validationFailures } = this.props;

        return (
            <div>
                The form was saved but was invalid with{" "}
                {validationFailures.length} errors.
            </div>
        );
    }

    renderSaveSuccessAlert(): any {
        return <div>The form has been saved successfully</div>;
    }

    handleOnSubmit(data: Dtos.Group) {
        const { hasBeenSubmitted } = this.state;
        const { saveGroup } = this.props;

        if (!hasBeenSubmitted) {
            this.setState({ hasBeenSubmitted: true });
        }

        if (data) {
            let changes: Dtos.Group = {} as Dtos.Group;
            saveGroup(Object.assign({ ...data }, changes));
        }
    }

    handleOnSubmitFailed(_data: Dtos.Group) {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    onCancel() {
        const { navigate } = this.props;

        navigate("/groups");
    }

    loadForm() {
        const { loadForm, group } = this.props;

        let extraData: Dtos.Group = {} as Dtos.Group;

        loadForm(reduxFormName, Object.assign({ ...group }, extraData));
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: GroupPageProps
): IGroupPageProps => {
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        match: ownProps.match,
        groupId:
            ownProps.match &&
            ownProps.match.params.groupId?.toString() != "create"
                ? ownProps.match.params.groupId
                : undefined!,

        history: ownProps.history,
        location: ownProps.location,

        group: group!,

        loadingGroup:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,

        savingGroup:
            state.groups.saveState &&
            state.groups.saveState.status == RequestState.Pending,
        saveGroupSuccess:
            state.groups.saveState &&
            state.groups.saveState.status == RequestState.Success,
        saveGroupFailure:
            state.groups.saveState &&
            state.groups.saveState.status == RequestState.Failure,

        creatingGroup:
            state.groups.createState &&
            state.groups.createState.status == RequestState.Pending,
        createGroupSuccess:
            state.groups.createState &&
            state.groups.createState.status == RequestState.Success,
        createGroupFailure:
            state.groups.createState &&
            state.groups.createState.status == RequestState.Failure,

        validatingGroup:
            state.groups.validateState &&
            state.groups.validateState.status == RequestState.Pending,

        user: state.user.data,
        permissions: permissions,
        lookups: state.groups.lookups,

        form: state.reduxForms.group,
        formState: state.groups.formState,
        formProperties: state.groups.formProperties,

        reduxFormState: state.reduxForms.formState.group,
        validationFailures: group ? state.groups.validationFailures : undefined,

        healthDistricts: state.healthDistricts.formFilteredData!,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: GroupPageProps
): IGroupPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),

        loadGroup: bindActionCreators(GroupActions.LoadGroupById, dispatch),
        clearGroup: bindActionCreators(GroupActions.Clear, dispatch),

        saveGroup: bindActionCreators(GroupActions.SaveGroup, dispatch),
        createGroup: bindActionCreators(GroupActions.CreateGroup, dispatch),
        validateGroup: bindActionCreators(GroupActions.ValidateGroup, dispatch),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),

        loadHealthDistricts: bindActionCreators(
            HealthDistrictActions.LoadHealthDistrict,
            dispatch
        ),
        clearHealthDistricts: bindActionCreators(
            HealthDistrictActions.Clear,
            dispatch
        )
    };
};

export default RouterHoc<IGroupPageParams, GroupPageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(GroupPage) as unknown as ComponentType<GroupPageProps>
) as ComponentType<any>;
